import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as store from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import { useParams } from "react-router-dom";
import { MuiDataGrid } from "../framework/muiDataGrid";
import { IWorkTimeDetailsPage } from "./workTimeBetaDetailsPageMain";
import * as workTimeBetaService from "../../services/workTimeBetaService";
import * as baseService from "../../services/baseService";
import {
    GridColDef,
    GridRenderCellParams,
    GridRowId,
    GridValueFormatterParams,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { Translations } from "../../models/translations";
import { Base } from "../../framework/base";
import MuiMenu from "../framework/muiMenu";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import { WorkTimeAutomationsIndicator } from "./workTimeAutomationsIndicator";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Undo as UndoIcon,
} from "@mui/icons-material";
import { WorkShiftTimeSlotItem } from "../../models/workShitTimeSlot/workShiftTimeSlotItem";
import { EnumHelper, WorkShiftTimeSlotState, WorkTimeTypeType } from "../../models/common/enums";
import { useTheme } from "@mui/material";
import { LocationPoint } from "../../models/common/locationPoint";
import {
    LocationMapDialog,
    LocationIcon,
} from "../framework/locationMapDialog";
import { WorkTimeEventIndicator } from "./workTimeEventIndicator";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import {
    deleteWorkShiftTimeSlot,
    setEditId,
    toggleSelectedId,
} from "../../store/workShiftTimeSlotSlice";
import { useTranslation } from "react-i18next";
import { AnyAction } from "redux";
import { deletableCategories } from "../../models/workTime/hourBookingCategory";
import { IWorkHoursItem } from "../../models/workTime/workHoursItem";
import { AppUtils } from "../../models/common/appUtils";
import {
    DataGridPremiumProps,
    useGridApiContext,
    useGridApiRef
} from "@mui/x-data-grid-premium";
import { useSelectCalculatedWorkHoursByDate } from "../../hooks/workTime/useSelectWorkHours";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { setShownColumns, setHoveredId } from "../../store/workTimeDetailsSlice";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import ReactDOM from "react-dom";
import { NativeSelect } from "@mui/material";
import { saveWorkShiftTimeSlot } from "../../services/workTimeBetaService";
import { showApiError, showApiSuccess } from "../framework/formUtils";
import { WorkTimeSaveData } from "../../models/workShitTimeSlot/workTimeSaveData";
import { useDebounce } from "../hooks/useDebounce";
import { ISalaryRowType } from "../../models/workShitTimeSlot/salaryRowType";
import { useAppOptionValueEquals } from "../../hooks/useAppOption";
import {
    OptionWorkTimeListAddressEnabled,
    optionWorkTimeListVehicleDataEnabled,
} from "../../models/common/consts";

interface ISelectedLocation {
    title: string;
    point: LocationPoint;
}

interface StartTimeColValue {
    startDate: string;
    endDate: string;
    hasOverlapping: boolean;
    label: string;
}

interface IWorkTimeDetailsListProps {
    reloadList: (slotIds?: string[]) => void;
    workShiftTimeSlotItems: WorkShiftTimeSlotItem[];
    salaryRowTypes: ISalaryRowType[];
}

export const formatTime = (time: string, date: string) =>
    Base.isSameDay(Base.dateStrToDayjsIgnoreTimezone(time), date)
        ? Base.dateStrToOriginalTimezoneTimeStr(time)
        : Base.dateStrToOriginalTimezoneDateTimeStr(time);

const locationIconColWidth = 50;
const startTimeColWidth = 155;

interface ActionCellProps {
    handleEdit: (id: string) => void;
    handleDelete: (id: string) => void;
    handleLock: (ids: string[], lock: boolean) => void;
    handleToggleDeleteWorkHour: (
        id: string,
        category: string,
        restore: boolean
    ) => void;
}

type WorkTimeDetailRow = WorkShiftTimeSlotItem & IWorkHoursItem &{
    deleted?: boolean;
}


function ActionCell(props: GridRenderCellParams<WorkTimeDetailRow, any, GridRowId> & ActionCellProps) {
    const {
        value,
        row,
        rowNode,
        handleEdit,
        handleDelete,
        handleLock,
        handleToggleDeleteWorkHour,
    } = props;
    const apiRef = useGridApiContext();
    const theme = useTheme();
    const { t } = useTranslation();

    if (rowNode.depth > 0 && !deletableCategories().includes(row.category))
        return null;

    if (rowNode.depth > 0)
        return (
            <IconButton
                onClick={() =>
                    handleToggleDeleteWorkHour(
                        row.workShiftTimeSlotId,
                        row.category,
                        !!row.deleted
                    )
                }
            >
                {row.deleted ? <UndoIcon /> : <DeleteIcon color="error" />}
            </IconButton>
        );

    const isLocked = EnumHelper.isEqual(
        row.state,
        WorkShiftTimeSlotState.Locked
    );

    const toggleShowCalculated =
        rowNode.type === "group"
            ? {
                label: rowNode.childrenExpanded
                    ? t("workTime.hideCalculatedWorkHours")
                    : t("workTime.showCalculatedWorkHours"),
                icon: rowNode.childrenExpanded ? (
                    <ExpandLessIcon color="primary" />
                ) : (
                    <ExpandMoreIcon color="primary" />
                ),
                onClick: () =>
                    apiRef.current.setRowChildrenExpansion(
                        row.id,
                        !rowNode.childrenExpanded
                    ),
            }
            : null;

    const items = isLocked
        ? [
            {
                label: Translations.DoUnlockAll,
                icon: <LockOpenIcon color="primary" />,
                onClick: () => handleLock([value as string], false),
            },
            toggleShowCalculated,
        ]
        : [
            {
                label: Translations.Edit,
                icon: <EditIcon color="primary" />,
                onClick: () => handleEdit(value as string),
            },
            {
                label: Translations.DoLockAll,
                icon: <LockIcon color="primary" />,
                onClick: () => handleLock([value as string], true),
            },
            toggleShowCalculated,
            { divider: true },
            {
                label: Translations.Remove,
                icon: <DeleteIcon color="error" />,
                onClick: () => handleDelete(value as string),
            },
        ];

    return (
        <MuiMenu
            label={
                isLocked ? (
                    <LockIcon
                        sx={{
                            color: theme.palette.primary.main,
                        }}
                    />
                ) : (
                    <MoreHorizIcon
                        sx={{
                            color: theme.palette.primary.main,
                        }}
                    />
                )
            }
            items={items}
        />
    );
}

export function WorkTimeDetailsList({
    reloadList,
    workShiftTimeSlotItems,
    salaryRowTypes,
}: IWorkTimeDetailsListProps) {
    const [loading, setLoading] = useState(false);
    const [isUpdateOngoing, setIsUpdateOngoing] = useState(false);
    const dispatch = useAppDispatch();
    const apiRef = useGridApiRef();
    const [vehicleDataEnabled] = useAppOptionValueEquals(
        optionWorkTimeListVehicleDataEnabled,
        true
    );

    const [addressEnabled] = useAppOptionValueEquals(
        OptionWorkTimeListAddressEnabled,
        true
    );

    const shownColumns = useAppSelector((state) => state.workTimeDetails.workTimeDetailsListShownColumns);
    const [
        overlappingWorkShiftTimeSlotItemIds,
        setOverlappingWorkShiftTimeSlotItemIds,
    ] = useState<string[]>();
    const { date, employeeId } = useParams<IWorkTimeDetailsPage>();
    const allLocked =
        workShiftTimeSlotItems.length > 0 &&
        workShiftTimeSlotItems.every((i) =>
            new WorkShiftTimeSlotItem(i).isLocked()
        );

    const [selectedLocation, setSelectedLocation] =
        useState<ISelectedLocation>();
    const { t } = useTranslation();
    const selectedId = store.useAppSelector(
        (state) => state.workShiftTimeSlot.selectedId
    );

    const workTimeTypes = store.useAppSelector(state => state.workShiftTimeSlot.workTimeTypes);

    const workHours = useSelectCalculatedWorkHoursByDate(
        employeeId,
        date,
        date
    );

    const rows = useMemo(
        () => [
            ...workHours
                .map((s) => {
                    const slot = workShiftTimeSlotItems.find(
                        (i) => i.id === s.workShiftTimeSlotId
                    );

                    return {
                        costCenterName:
                            s.costCenterId === slot?.costCenterId
                                ? slot?.costCenterName
                                : null,
                        ...s,
                        id: slot
                            ? // To avoid duplicate ids:
                              // In case there is slot spanning multiple days,
                              // there could be many same category additions for same slot with different timestamps.
                              `${slot.id}.${s.categoryId}_${s.salaryRowTypeId}_${s.startTime}`
                            : null,
                    };
                })
                .filter((s) => s.id),
            ...workShiftTimeSlotItems.flatMap((i) => [
                i,
                ...i.ignoredCategories.map((c) => ({
                    id: `${i.id}.deleted-${c}`,
                    workShiftTimeSlotId: i.id,
                    category: c.toString(),
                    deleted: true,
                })),
            ]),
        ],
        [workShiftTimeSlotItems, workHours]
    );

    useEffect(() => {
        setOverlappingWorkShiftTimeSlotItemIds(
            workShiftTimeSlotItems
                .filter(
                    (item) =>
                        !item.isCustomType &&
                        workShiftTimeSlotItems.some(
                            (item2) =>
                                !item2.isCustomType &&
                                Base.isOverlappingDate(
                                    item.startDate,
                                    item.endDate,
                                    item2.startDate,
                                    item2.endDate
                                ) &&
                                item.id !== item2.id
                        )
                )
                .map((item) => item.id)
        );
    }, [workShiftTimeSlotItems.map((i) => i.rowId).join(",")]);

    const handleLocationClick = useCallback(
        (locationName: string, latitude: number, longitude: number) => {
            setSelectedLocation({
                title: locationName || Translations.Location,
                point: LocationPoint.createLocationPoint(
                    Base.getGuid(),
                    latitude,
                    longitude,
                    0,
                    locationName
                ),
            });
        },
        []
    );

    const updateWorkTime = (rowData: WorkTimeDetailRow) => (event) => {
        setIsUpdateOngoing(true);
        const {
            target: { value },
        } = event;
        const saveData = mapFormItemToSaveData(rowData, value as string);
        const previousValue = rowData.workTimeTypeId;
        rowData.workTimeTypeId = value;
        saveWorkShiftTimeSlot(saveData, false, false)
            .then(() => {
                showApiSuccess(Translations.SaveSuccess);
            })
            .catch((error) => {
                rowData.workTimeTypeId = previousValue;
                showApiError(error);
            })
            .finally(() => {
                setIsUpdateOngoing(false);
                reloadList([rowData.id]);
            });
    };

    const mapFormItemToSaveData = (rowData: WorkShiftTimeSlotItem, workTimeTypeId: string): WorkTimeSaveData => {
        return {
            id: rowData.id,
            rowId: rowData.rowId,
            employeeId: rowData.employeeId,
            vehicleId: rowData.vehicleId,
            workTimeTypeId: workTimeTypeId,
            salaryRowTypeId: rowData.salaryRowTypeId,
            costCenterId: rowData.costCenterId,
            workOrderId: rowData.workOrderId,
            comment: rowData.comment,
            startDate: rowData.startDate ? Base.dateStrToOriginalTimezoneJsonDateTime(rowData.startDate) : null,
            endDate: rowData.endDate ? Base.dateStrToOriginalTimezoneJsonDateTime(rowData.endDate) : null,
            timeZoneName: rowData.timeZoneName,
        };
    };

    const handleDelete = useCallback((id: string, overwrite = false) => {
        if (!id) return;
        store.customStore.dispatch(
            storeActions.setConfirmation(
                ConfirmationDialogType.Warning,
                Translations.Warning,
                overwrite
                    ? t("workTime.confirmDeleteForModifiedShift")
                    : Translations.WorkShiftTimeSlotRemoveConfirmation,
                () => {
                    store.customStore.dispatch(
                        storeActions.clearConfirmation()
                    );
                    setLoading(true);
                    let conflict = false;

                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    store.customStore
                        .dispatch(
                            deleteWorkShiftTimeSlot({
                                id,
                                allowShiftOverwrite: overwrite,
                            }) as unknown as AnyAction
                        )
                        .unwrap()
                        .then(() => {
                            store.customStore.dispatch(
                                storeActions.showSuccessMessage(
                                    t("workTimeDeleteSuccessful")
                                )
                            );
                        })
                        .catch((error) => {
                            if (
                                error.message ===
                                Translations.WorkShiftDateHasBeenModified
                            ) {
                                conflict = true;
                                handleDelete(id, true);
                            } else {
                                store.customStore.dispatch(
                                    storeActions.showErrorMessage(error.message as string)
                                );
                            }
                        })
                        .finally(() => {
                            if (!conflict) {
                                reloadList(
                                    workShiftTimeSlotItems.map((s) => s.id)
                                );
                            }
                            setLoading(false);
                        });
                },
                () => {
                    store.customStore.dispatch(
                        storeActions.clearConfirmation()
                    );
                }
            )
        );
    }, [workShiftTimeSlotItems]);

    const handleToggleDeleteWorkHour = useCallback(
        (
            id: string,
            category: string,
            restore: boolean
        ) => {
            if (!id || !category) return;
            store.customStore.dispatch(
                storeActions.setConfirmation(
                    ConfirmationDialogType.Warning,
                    Translations.Warning,
                    t(
                        restore
                            ? "workTime.workHourRestoreConfirmation"
                            : "workTime.workHourRemoveConfirmation",
                        {
                            category: t(`hourBookingCategory.${category}`),
                        }
                    ),
                    () => {
                        store.customStore.dispatch(
                            storeActions.clearConfirmation()
                        );
                        setLoading(true);

                        workTimeBetaService
                            .deleteCategoryFromWorkShiftTimeSlot(
                                id,
                                category,
                                restore
                            )
                            .then(() => {
                                store.customStore.dispatch(
                                    storeActions.showSuccessMessage(
                                        t(
                                            restore
                                                ? "workTime.workHourRestoreSuccesful"
                                                : "workTime.workHourDeleteSuccesful",
                                            {
                                                category: t(
                                                    `hourBookingCategory.${category}`
                                                ),
                                            }
                                        )
                                    )
                                );
                            })
                            .catch((error) => {
                                store.customStore.dispatch(
                                    storeActions.showErrorMessage(
                                        error.message as string
                                    )
                                );
                            })
                            .finally(() => {
                                setLoading(false);
                                reloadList();
                            });
                    },
                    () => {
                        store.customStore.dispatch(
                            storeActions.clearConfirmation()
                        );
                    }
                )
            );
        },
        []
    );

    const handleEdit = useCallback(
        (id: string) => {
            store.customStore.dispatch(setEditId(id));
        },
        [store.customStore.dispatch]
    );

    const getAddress = (startAddress: string, endAddress: string) => {
        // Show address only if end & start location are same and set.
        // This means that work task is done in same location and
        // address information is relevant for user.
        if (!startAddress || !endAddress || (startAddress !== endAddress)) {
            return "";
        }

        return endAddress;
    };

    const handleLock = useCallback((ids: string[], lock: boolean) => {
        if (ids.length === 0) return;
        setLoading(true);
        (lock
            ? workTimeBetaService.lockWorkTimes(ids)
            : workTimeBetaService.unlockWorkTimes(ids)
        )
            .then((success) => {
                store.customStore.dispatch(
                    storeActions.showSuccessMessage(success.message)
                );
                reloadList();
            })
            .catch((error) => {
                store.customStore.dispatch(
                    storeActions.showErrorMessage(
                        baseService.getErrorMessageFromError(error)
                    )
                );
                return null;
            })
            .finally(() => setLoading(false));
    }, []);

    const cols: GridColDef<WorkTimeDetailRow>[] = useMemo(
        () =>
            [
                {
                    field: "automatic",
                    headerName: "",
                    width: 60,
                    renderCell: (params: GridRenderCellParams<WorkTimeDetailRow>) =>
                        params.rowNode.depth > 0 ? null : (
                            <WorkTimeAutomationsIndicator slot={params.row} />
                        ),
                    sortable: false,
                    filterable: false,
                    disableColumnMenu: true,
                },
                {
                    field: "startLocationName",
                    headerName: Translations.StartLocation,
                    width: locationIconColWidth,
                    renderCell: (
                        params: GridRenderCellParams<WorkTimeDetailRow>
                    ) => (
                        <LocationIcon
                            title={params.row.startLocationName}
                            latitude={params.row.startLatitude}
                            longitude={params.row.startLongitude}
                            handleClick={handleLocationClick}
                        />
                    ),
                    sortable: false,
                    filterable: false,
                    disableColumnMenu: true,
                },
                {
                    field: "startTime",
                    headerName: Translations.WorkTime,
                    width: startTimeColWidth,
                    valueGetter: (
                        params: GridValueGetterParams<WorkTimeDetailRow>
                    ): StartTimeColValue => {
                        return params.rowNode.depth > 0
                            ? null
                            : {
                                startDate: params.row.startDate,
                                endDate: params.row.endDate,
                                hasOverlapping:
                                      overlappingWorkShiftTimeSlotItemIds?.includes(
                                          params.row.id
                                      ),
                                label: `${formatTime(
                                      params.row.startDate,
                                      date
                                  )} - ${
                                      params.row.state !==
                                      (WorkShiftTimeSlotState.InProgress as number)
                                          ? formatTime(params.row.endDate, date)
                                          : ""
                                  } ${Base.dateTzOffsetIfNotLocal(
                                      params.row.startDate,
                                      params.row.timeZoneName
                                  )}`,
                            };
                    },
                    valueFormatter: (
                        params: GridValueFormatterParams<StartTimeColValue>
                    ) => params.value?.label,
                    sortComparator: (
                        a: WorkShiftTimeSlotItem,
                        b: WorkShiftTimeSlotItem
                    ) => Base.dayjsCompare(a?.startDate, b?.startDate),
                    renderCell: (
                        params: GridRenderCellParams<WorkShiftTimeSlotItem>
                    ) =>
                        params.value ? (
                            <Tooltip
                                title={`${
                                    (params.value as StartTimeColValue)?.label
                                } ${params.row.timeZoneDisplayName}`}
                            >
                                <span
                                    className={`${
                                        params.value?.hasOverlapping
                                            ? "text-danger"
                                            : ""
                                    }`}
                                    style={{
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {params.value?.label}
                                </span>
                            </Tooltip>
                        ) : null,
                },
                {
                    field: "endLocationName",
                    headerName: Translations.EndLocation,
                    width: locationIconColWidth,
                    renderCell: (
                        params: GridRenderCellParams<WorkShiftTimeSlotItem>
                    ) => (
                        <LocationIcon
                            title={params.row.endLocationName}
                            latitude={params.row.endLatitude}
                            longitude={params.row.endLongitude}
                            handleClick={handleLocationClick}
                        />
                    ),
                    sortable: false,
                    filterable: false,
                    disableColumnMenu: true,
                },
                addressEnabled
                    ? {
                        field: "workStageAddress",
                        headerName: Translations.Address,
                        flex: 1,
                        valueGetter: (
                            params: GridValueGetterParams<WorkTimeDetailRow>
                        ) =>
                            getAddress(
                                params.row.startLocationName,
                                params.row.endLocationName
                            ),
                    }
                    : null,
                {
                    field: "timeZoneDisplayName",
                    headerName: Translations.TimeZone,
                    flex: 1,
                },
                {
                    field: "endTime",
                    headerName: `${Translations.Hours}`,
                    flex: 1,
                    valueGetter: (
                        params: GridValueGetterParams<WorkTimeDetailRow>
                    ) => {
                        const srt = salaryRowTypes.find(
                            (srt) => srt.id === params.row.salaryRowTypeId
                        );

                        return params.rowNode.depth > 0
                            ? params.row.amount
                                ? {
                                    time: srt
                                        ? AppUtils.getDisplayedNumberBasedOnUnit(
                                            params.row.amount,
                                            srt.measureUnit,
                                            srt.decimals
                                        )
                                        : AppUtils.getDurationStrByDurationMinShort(
                                            params.row.amount
                                        ),
                                    edited: false,
                                }
                                : null
                            : {
                                time: new WorkShiftTimeSlotItem(
                                    params.row
                                ).getDurationStr(),
                                edited: params.row.edited,
                            };
                    },
                    renderCell: (
                        params: GridRenderCellParams<WorkShiftTimeSlotItem>
                    ) => (
                        <>
                            <WorkTimeEventIndicator
                                id={params.row.id}
                                baseLabel={params.value?.time}
                                edited={params.row.edited}
                                orgStartTime={params.row.orgStartDate}
                                orgEndTime={params.row.orgEndDate}
                            />
                        </>
                    ),
                },
                {
                    field: "workOrderName",
                    headerName: Translations.CostCenter,
                    flex: 1,
                    valueGetter: (
                        params: GridValueGetterParams<WorkTimeDetailRow>
                    ) =>
                        params.row.costCenterName ||
                        (params.row.workOrderName
                            ? `${params.row.workOrderNumber} ${params.row.workOrderName}`
                            : ""),
                },
                {
                    field: "customerName",
                    headerName: Translations.Customer,
                    flex: 1,
                },
                {
                    field: "workTimeTypeName",
                    headerName: Translations.WorkTimeType,
                    width: 150,
                    valueGetter: (
                        params: GridValueGetterParams<WorkTimeDetailRow>
                    ) =>
                        params.rowNode.depth > 0
                            ? params.row.category
                            : new WorkShiftTimeSlotItem(
                                params.row
                            ).getWorkTimeTypeName(),
                    renderCell: (
                        params: GridRenderCellParams<WorkTimeDetailRow>
                    ) =>
                        params.rowNode.depth > 0 && !params.row.deleted ? (
                            t(`hourBookingCategory.${params.value}`)
                        ) : params.row.deleted ? (
                            <Tooltip title={t("common.removed")}>
                                <span className="text-line-through">
                                    {t(`hourBookingCategory.${params.value}`)}
                                </span>
                            </Tooltip>
                        ) : params.rowNode.depth === 0 &&
                          params.row.state !==
                              (WorkShiftTimeSlotState.Locked as number) ? (
                                  <div
                                      title={params.value}
                                      className="MuiDataGrid-cellContent"
                                  >
                                      {!params.row.isCustomType &&
                                workTimeTypes?.length > 0 ? (
                                    <NativeSelect
                                        sx={{ fontSize: "0.75rem" }}
                                        disabled={isUpdateOngoing}
                                        value={
                                            params.row.workTimeTypeId
                                                ? params.row.workTimeTypeId
                                                : ""
                                        }
                                        onChange={updateWorkTime(params.row)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        <option key="none" value="" />
                                        {workTimeTypes?.map((t) => (
                                            <option
                                                key={t.type}
                                                value={t.workTimeTypeId}
                                            >
                                                {t.name}
                                            </option>
                                        ))}
                                    </NativeSelect>
                                          ) : (
                                              params.value
                                          )}
                                  </div>
                            ) : (
                                <div
                                    title={params.value}
                                    className="MuiDataGrid-cellContent"
                                >
                                    {params.value}
                                </div>
                            ),
                },
                {
                    field: "salaryRowTypeName",
                    headerName: t("workTime.categoryCustomAddition"),
                    flex: 1,
                },
                {
                    field: "comment",
                    headerName: Translations.Description,
                    flex: 2,
                },
                {
                    field: "vehicleRegisterNumber",
                    headerName: Translations.Vehicle,
                    flex: 1,
                    valueGetter: (params) =>
                        `${params.row.vehicleRegisterNumber || ""}${
                            params.row.vehicleBrand
                                ? `, ${params.row.vehicleBrand}`
                                : ""
                        }`,
                },
                vehicleDataEnabled
                    ? {
                        field: "distance",
                        headerName: t("reporting.distance"),
                        flex: 1,
                        valueGetter: (
                            params: GridValueGetterParams<WorkTimeDetailRow>
                        ) => {
                            if (
                                !params.row.vehicleId ||
                                  !params.row.vehicleId.isGuid() ||
                                  params.row.workTimeTypeType ===
                                      (WorkTimeTypeType.Break as number)
                            ) {
                                return "";
                            } else {
                                return params.row.distance !== null &&
                                      params.row.distance >= 0
                                    ? `${params.row.distance.toFixed(1)}`
                                    : "-";
                            }
                        },
                    }
                    : null,
                vehicleDataEnabled
                    ? {
                        field: "fuelConsumed",
                        headerName: t("reporting.consumption"),
                        flex: 1,
                        valueGetter: (
                            params: GridValueGetterParams<WorkTimeDetailRow>
                        ) => {
                            if (
                                !params.row.vehicleId ||
                                  !params.row.vehicleId.isGuid() ||
                                  params.row.workTimeTypeType ===
                                      (WorkTimeTypeType.Break as number)
                            ) {
                                return "";
                            } else {
                                return params.row.fuelConsumed !== null &&
                                      params.row.fuelConsumed >= 0
                                    ? `${params.row.fuelConsumed.toFixed(1)}`
                                    : "-";
                            }
                        },
                    }
                    : null,
                {
                    field: "id",
                    headerName: "",
                    width: 75,
                    sortable: false,
                    filterable: false,
                    disableColumnMenu: true,
                    hideable: false,
                    renderCell: (params) => (
                        <ActionCell
                            {...params}
                            handleLock={handleLock}
                            handleEdit={handleEdit}
                            handleDelete={handleDelete}
                            handleToggleDeleteWorkHour={
                                handleToggleDeleteWorkHour
                            }
                        />
                    ),
                },
            ].filter(Boolean),
        [
            handleEdit,
            handleLock,
            handleLocationClick,
            handleDelete,
            handleToggleDeleteWorkHour,
            date,
            overlappingWorkShiftTimeSlotItemIds?.join(","),
            salaryRowTypes,
            vehicleDataEnabled,
            addressEnabled,
        ]
    );

    const onRowSelect = (id: string | undefined) => {
        if (id?.includes(".")) return; // Skip children
        store.customStore.dispatch(toggleSelectedId(id));
    };

    const groupingColDef: DataGridPremiumProps["groupingColDef"] = {
        renderCell: () => null,
    };

    useEffect(() => {
        let unsubscribeRowMouseEnter: (() => void) | undefined;
        let unsubscribeRowMouseLeave: (() => void) | undefined;

        if (apiRef.current?.subscribeEvent) {
            unsubscribeRowMouseEnter = apiRef.current.subscribeEvent("rowMouseEnter", (params) =>
                dispatch(
                    setHoveredId(params.id as string)
                )
            );
            unsubscribeRowMouseLeave = apiRef.current.subscribeEvent("rowMouseLeave", () =>
                dispatch(
                    setHoveredId(null)
                )
            );
        }

        return () => { // returns a cleaning callback that unsubscribes the given handler when called (prevent multiple registrations)
            if (unsubscribeRowMouseEnter) unsubscribeRowMouseEnter();
            if (unsubscribeRowMouseLeave) unsubscribeRowMouseLeave();
        };
    }, [apiRef, dispatch]);

    useEffect(() => {
        // Forces auto sizing for datagrid columns after all data is loaded
        autosizeTableColumns();
    }, [apiRef, rows, date]);

    const autosizeColumnsByName = ["workTimeTypeName"];
    const autosizeTableColumns = useDebounce(() => {
        ReactDOM.flushSync(() => {
            void apiRef.current?.autosizeColumns({
                columns: autosizeColumnsByName,
                includeOutliers: true,
                includeHeaders: true,
            });
        });
    }, 500);

    if (workShiftTimeSlotItems.length === 0 || !(salaryRowTypes?.length > 0)) {
        return null;
    }

    return (
        <>
            <LoadingIndicator loading={loading} />
            <MuiDataGrid
                apiRef={apiRef}
                density="compact"
                className="work-entries-table"
                sx={{
                    fontSize: "0.75rem",
                }}
                treeData
                rowHeight={60}
                getTreeDataPath={(row : WorkTimeDetailRow) => row.id.split(".")}
                disableChildrenSorting
                disableChildrenFiltering
                groupingColDef={groupingColDef}
                onRowSelectionModelChange={(newRowSelectionModel) =>
                    onRowSelect(newRowSelectionModel[0] as string)
                }
                rowSelectionModel={[selectedId].filter(Boolean)}
                columnVisibilityModel={shownColumns}
                onColumnVisibilityModelChange={(newModel) => {
                    dispatch(setShownColumns(newModel));
                }}
                title={Translations.WorkingTimeRecords}
                rows={rows}
                columns={cols}
                autosizeOnMount={true}
                autosizeOptions={{
                    columns: autosizeColumnsByName,
                    includeOutliers: true,
                    includeHeaders: true,
                }}
                hideFooter
                initialState={{
                    sorting: {
                        sortModel: [
                            {
                                field: "startTime",
                                sort: "asc",
                            },
                        ],
                    },
                }}
                customToolbarButtons={[
                    {
                        key: "lock",
                        startIcon: allLocked ? <LockOpenIcon /> : <LockIcon />,
                        onClick: () =>
                            handleLock(
                                workShiftTimeSlotItems.map((i) => i.id),
                                !allLocked
                            ),
                        children: allLocked
                            ? Translations.UnlockAll
                            : Translations.LockAll,
                    },
                ]}
            />
            {selectedLocation ? (
                <LocationMapDialog
                    title={selectedLocation.title}
                    points={[selectedLocation.point]}
                    selectedIds={[selectedLocation.point?.id]}
                    onClose={() => setSelectedLocation(null)}
                />
            ) : null}
        </>
    );
}
